/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react"
import type { RefObject } from "react"

import { type LegacyAnimationControls, useAnimation, useReducedMotion } from "framer-motion"
import includes from "lodash-es/includes"
import { useBoolean, useIntersection, useIsomorphicLayoutEffect } from "react-use"

import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import {
    EAppAnimationName,
    type IAppState,
    type TAppDispatcher,
    useAppDispatcher,
    useAppSelector
} from "@/3514/store/app"

type _TParams = {
    animationName: EAppAnimationName
    updateKey: number | undefined
}
function useListAnimation(params: _TParams): {
    containerRef: RefObject<HTMLDivElement>
    controls: LegacyAnimationControls
    isMotionReduced: boolean
    isMobile: boolean
    wasAnimated: boolean
} {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const isMotionReduced: boolean = useReducedMotion()

    const controls: LegacyAnimationControls = useAnimation()

    const { completeAnimation: completeAppAnimation }: TAppDispatcher = useAppDispatcher()

    const { animations: appAnimations }: IAppState = useAppSelector()

    const isReadyState: ReturnType<typeof useBoolean> = useBoolean(false)

    const isMountedRef: RefObject<boolean> = useRef<boolean>(false)

    const intersection: IntersectionObserverEntry = useIntersection(containerRef, {
        root: null,
        rootMargin: "10px 0px 0px 0px",
        threshold: 0.1
    })

    const wasAnimated: boolean = includes<EAppAnimationName>(appAnimations, params.animationName)
    const shouldAnimate: boolean = breakpoints.xs || breakpoints.sm ? false : !wasAnimated && !isMotionReduced

    useIsomorphicLayoutEffect((): void => {
        if (shouldAnimate) controls.set("hidden")
        else controls.set("shown")
    }, [shouldAnimate, wasAnimated, isMotionReduced, controls])

    useEffect((): void => {
        if (!params.updateKey) return
        if (!isMountedRef.current) return

        controls.start("hidden").then((): Promise<void> => controls.start("shown"))
    }, [params.updateKey, controls])

    useEffect((): void => {
        isMountedRef.current = true
    }, [])

    useEffect((): void => {
        if (!shouldAnimate) return

        if (intersection?.isIntersecting) isReadyState[1](true)
    }, [intersection?.isIntersecting, shouldAnimate])

    useEffect((): void => {
        if (wasAnimated || !shouldAnimate) controls.set("shown")
        else if (isReadyState[0])
            controls.start("shown").then((): void => void completeAppAnimation(params.animationName))
    }, [controls, isReadyState[0], wasAnimated, params.animationName, shouldAnimate])

    return { containerRef, controls, isMotionReduced, isMobile: breakpoints.xs || breakpoints.sm, wasAnimated }
}

export default { useListAnimation }
